import { Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getOrderAsExcel, getOrderAsPDF } from '../../../../api/orders';
import { useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { withVendorAddedStatus } from './itemStatusDisplay';

DownloadOrderDropdown.propTypes = {
  setAnchorEl: PropTypes.func,
};

export default function DownloadOrderDropdown({ setAnchorEl }) {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { orderById } = useSelector((state) => state.orders || {});
  const { items = [], orderAddress, orderNumber } = orderById[id] || {};

  const [vendorIds, setVendorIds] = useState([]);
  const [useAllVendors, setUseAllVendors] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('pdf');
  const vendorIdFromUrl = searchParams.get('vendorId');
  const isViewByVendor = !!vendorIdFromUrl && vendorIdFromUrl !== 'all';

  const vendors = useMemo(() => {
    if (!items) return [];
    const vendorArray = items?.map((item) => ({ vendor: item.vendor, vendorId: item.vendorId }));

    const uniqueVendors = Array.from(new Set(vendorArray.map((v) => v.vendorId))).map((id) =>
      vendorArray.find((v) => v.vendorId === id)
    );

    return uniqueVendors;
  }, [orderById]);

  const selectedVendor = useMemo(
    () => vendors.find((vendor) => String(vendor.vendorId) === vendorIdFromUrl),
    [vendors, vendorIdFromUrl]
  );

  useEffect(() => {
    if (isViewByVendor) {
      setVendorIds(selectedVendor ? [selectedVendor.vendorId] : []);
      setUseAllVendors(false);
      return;
    }
    if (useAllVendors) setVendorIds(vendors.map((vendor) => vendor.vendorId));
    else setVendorIds([]);
  }, [useAllVendors, vendors, isViewByVendor, selectedVendor]);

  const downloadOrder = async () => {
    const itemsToAdd = items.filter((i) => vendorIds.includes(i.vendorId))
      .map((item) => ({ ...item, statusCode: withVendorAddedStatus(item) }));

    if (itemsToAdd.length > 0) {
      const vendorId = vendorIds.length === 1 ? vendorIds[0] : 0;
      const res =
        downloadFormat === 'pdf'
          ? await getOrderAsPDF({
              orderId: id,
              items: itemsToAdd,
              orderAddress,
              vendorId,
            }).catch(() => enqueueSnackbar('Order PDF failed to download', { variant: 'error' }))
          : await getOrderAsExcel({ orderId: id, items: itemsToAdd, address: orderAddress }).catch(() =>
              enqueueSnackbar('Order Excel failed to download', { variant: 'error' })
            );

      setAnchorEl(null);

      if (res.status === 200) {
        const blob = new Blob([res.data], { type: `application/${downloadFormat}` });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${orderNumber || id}.${downloadFormat}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } else {
      enqueueSnackbar(isViewByVendor ? 'Vendor not found for this view' : 'No vendor selected', { variant: 'warning' });
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.25}>
        <Typography sx={{ fontSize: '14px', color: 'secondary.main', letterSpacing: 0.4, pl: 1.4 }}>Vendor</Typography>
        {isViewByVendor ? (
          <Typography sx={{ fontSize: '0.875rem', color: 'grey.700', pl: 1.4 }}>
            {selectedVendor?.vendor ?? 'Current vendor'}
          </Typography>
        ) : (
          <>
            <FormControlLabel
              sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'grey.700' } }}
              control={
                <Checkbox size="small" checked={useAllVendors} onChange={() => setUseAllVendors((useAll) => !useAll)} />
              }
              label="All Vendors"
            />
            {vendors?.map((vendor) => (
              <FormControlLabel
                key={vendor.vendorId}
                sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'grey.700' } }}
                control={
                  <Checkbox
                    size="small"
                    checked={vendorIds?.includes(vendor.vendorId)}
                    onChange={() =>
                      vendorIds?.includes(vendor.vendorId)
                        ? setVendorIds(vendorIds?.filter((v) => v !== vendor.vendorId))
                        : setVendorIds([...vendorIds, vendor.vendorId])
                    }
                  />
                }
                label={vendor.vendor}
              />
            ))}
          </>
        )}
      </Stack>

      <Stack spacing={0.25}>
        <Typography sx={{ fontSize: '14px', color: 'secondary.main', letterSpacing: 0.4, pl: 1.4 }}>Format</Typography>
        <FormControlLabel
          sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'grey.700' } }}
          control={
            <Checkbox
              size="small"
              checked={downloadFormat === 'pdf'}
              onChange={() => setDownloadFormat(downloadFormat === 'pdf' ? 'xlsx' : 'pdf')}
            />
          }
          label="PDF"
        />
        <FormControlLabel
          sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'grey.700' } }}
          control={
            <Checkbox
              size="small"
              checked={downloadFormat === 'xlsx'}
              onChange={() => setDownloadFormat(downloadFormat === 'pdf' ? 'xlsx' : 'pdf')}
            />
          }
          label="Excel"
        />
      </Stack>

      <Button
        fullWidth
        variant="contained"
        color="secondary"
        onClick={downloadOrder}
        startIcon={<Iconify icon="material-symbols:download" />}
        sx={{ borderRadius: 1, fontSize: '0.875rem', py: 0.75 }}
      >
        Download
      </Button>
    </Stack>
  );
}
